<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import ConnectionQR from './ConnectionQR.svelte';
  import { beginNostrConnect, connectBunker, connectNIP07, connectNcryptsec, connectNsec, recipientPermissions, type PendingNostrConnect, type SignerConnection } from './signers';

  let { relays, managedSignerURL = '', bitcoinPlusPlusPersonID = '', bitcoinPlusPlusEnabled = false, returnPath = '/', expectedPubkey = '', onconnected }: { relays: string[]; managedSignerURL?: string; bitcoinPlusPlusPersonID?: string; bitcoinPlusPlusEnabled?: boolean; returnPath?: string; expectedPubkey?: string; onconnected: (connection: SignerConnection) => void } = $props();
  const managedSignerHref = $derived(managedSignerURL && bitcoinPlusPlusPersonID ? `${managedSignerURL}/?tenant=person&tenant_id=${encodeURIComponent(bitcoinPlusPlusPersonID)}&return_to=${encodeURIComponent(returnPath)}${/^[0-9a-f]{64}$/.test(expectedPubkey) ? `&expected_pubkey=${expectedPubkey}` : ''}` : '');
  const bitcoinPlusPlusLoginHref = $derived(`/api/auth/btcpp/start?return_to=${encodeURIComponent(returnPath)}`);
  let mode = $state<'choose' | 'nsec' | 'unlock' | 'bunker' | 'connect'>('choose');
  let bunker = $state('');
  let nsec = $state('');
  let savedNcryptsec = $state('');
  let passphrase = $state('');
  let pending = $state<PendingNostrConnect | null>(null);
  let busy = $state(false);
  let error = $state('');
  let nip07Available = $state(typeof window !== 'undefined' && Boolean(window.nostr));

  onDestroy(() => pending?.cancel());
  onMount(() => {
    savedNcryptsec = localStorage.getItem('btcpp.badges.ncryptsec') ?? '';
    const returnedBunker = new URLSearchParams(location.search).get('managed_bunker')?.trim() ?? '';
    if (returnedBunker) {
      const cleanURL = new URL(location.href);
      cleanURL.searchParams.delete('managed_bunker');
      history.replaceState(null, '', `${cleanURL.pathname}${cleanURL.search}${cleanURL.hash}`);
      bunker = returnedBunker;
      void finish(connectBunker(returnedBunker, undefined, recipientPermissions));
    }
  });

  async function finish(connection: Promise<SignerConnection>) {
    busy = true;
    error = '';
    try {
      onconnected(await connection);
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not connect the signer.';
      busy = false;
    }
  }

  function createConnection() {
    mode = 'connect';
    try {
      pending = beginNostrConnect(relays, recipientPermissions, 'Badge Studio acceptance');
      void finish(pending.connected);
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not create the connection.';
    }
  }

  function chooseMode() {
    pending?.cancel();
    pending = null;
    mode = 'choose';
		nsec = '';
  }

  function useNsec() {
		const value = nsec;
		nsec = '';
		void finish(connectNsec(value));
	}

  function unlockSaved() {
		const value = passphrase;
		passphrase = '';
		void finish(connectNcryptsec(savedNcryptsec, value));
	}

  async function copy() {
    if (pending) await navigator.clipboard.writeText(pending.uri);
  }
</script>

<div class="recipient-signer">
  {#if mode === 'choose'}
	{#if managedSignerHref}<a href={managedSignerHref} target="_blank" rel="noopener noreferrer"><span>Bitcoin++ key vault</span><small>recommended · create, import, or unlock your key</small><i>↗</i></a>{:else if bitcoinPlusPlusEnabled}<a href={bitcoinPlusPlusLoginHref}><span>Use Bitcoin++</span><small>sign in to open your key vault</small><i>→</i></a>{/if}
	{#if savedNcryptsec}<button type="button" onclick={() => mode = 'unlock'} disabled={busy}><span>Unlock saved key</span><small>NIP-49</small><i>→</i></button>{/if}
    <button type="button" onclick={() => finish(connectNIP07())} disabled={busy || !nip07Available}><span>Browser signer</span><small>{nip07Available ? 'NIP-07 · ready' : 'NIP-07 · unavailable'}</small><i>→</i></button>
    <button type="button" onclick={() => mode = 'bunker'} disabled={busy}><span>Existing remote signer</span><small>NIP-46</small><i>→</i></button>
    <button type="button" onclick={createConnection} disabled={busy}><span>New connection request</span><small>NIP-46</small><i>→</i></button>
    <button type="button" onclick={() => mode = 'nsec'} disabled={busy}><span>Paste an nsec</span><small>local to this tab</small><i>→</i></button>
  {:else if mode === 'nsec'}
    <label for="recipient-nsec">Nostr secret key</label>
    <input id="recipient-nsec" class="secret-input" type="password" bind:value={nsec} autocomplete="off" autocapitalize="none" spellcheck="false" placeholder="nsec1…" />
    <p class="key-note">The key is held only in this tab’s memory and is never uploaded or saved.</p>
    <div class="actions"><button type="button" onclick={chooseMode}>Back</button><button class="dark" type="button" onclick={useNsec} disabled={!nsec.trim() || busy}>Use this key →</button></div>
  {:else if mode === 'unlock'}
	<label for="recipient-passphrase">Encryption passphrase</label><input id="recipient-passphrase" class="secret-input" type="password" bind:value={passphrase} autocomplete="current-password" />
	<div class="actions"><button type="button" onclick={chooseMode}>Back</button><button class="dark" type="button" onclick={unlockSaved} disabled={!passphrase || busy}>Unlock →</button></div>
  {:else if mode === 'bunker'}
    <label for="recipient-bunker">Bunker URL or NIP-05 address</label>
    <textarea id="recipient-bunker" bind:value={bunker} rows="3" placeholder="bunker://… or signer@example.com"></textarea>
    <div class="actions"><button type="button" onclick={chooseMode}>Back</button><button class="dark" type="button" onclick={() => finish(connectBunker(bunker, undefined, recipientPermissions))} disabled={!bunker.trim() || busy}>Connect →</button></div>
  {:else if pending}
    <ConnectionQR value={pending.uri} />
    <code>{pending.uri}</code>
    <div class="actions"><button type="button" onclick={copy}>Copy request</button><a class="dark" href={pending.uri}>Open in signer ↗</a></div>
    <p class="waiting"><i></i> Waiting for approval…</p>
  {/if}
  {#if busy && mode === 'choose'}<p class="waiting"><i></i> Waiting for approval…</p>{/if}
  {#if error}<p class="error" role="alert">{error}</p>{/if}
</div>

<style>
  .recipient-signer { display: grid; border-top: 1px solid var(--line); }
  .recipient-signer > :is(button, a) { display: grid; grid-template-columns: 1fr auto 28px; align-items: center; gap: 12px; min-height: 64px; padding: 10px 3px; border: 0; border-bottom: 1px solid var(--line); background: transparent; color: var(--ink); text-align: left; text-decoration: none; cursor: pointer; }
  .recipient-signer > :is(button, a) span { font-family: var(--display); font-size: 18px; }
  .recipient-signer small { color: var(--violet); font: 9px var(--mono); }
  .recipient-signer :is(button, a) i { font-style: normal; }
  label { margin-top: 18px; font-size: 11px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
  textarea { width: 100%; margin-top: 8px; padding: 13px; border: 1px solid var(--line-strong); border-radius: 0; background: var(--surface-soft); font: 16px var(--mono); }
  .secret-input { width: 100%; min-height: 48px; margin-top: 8px; padding: 12px 13px; border: 1px solid var(--line-strong); background: var(--surface-soft); color: var(--ink); font: 16px var(--mono); }
  .key-note { color: var(--muted); font-size: 12px; line-height: 1.5; }
  code { max-height: 130px; margin-top: 17px; overflow: auto; padding: 13px; background: var(--surface-soft); font: 9px/1.5 var(--mono); overflow-wrap: anywhere; }
  .actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 9px; margin-top: 14px; }
  .actions button, .actions a { min-height: 44px; display: inline-flex; align-items: center; padding: 9px 15px; border: 1px solid var(--line-strong); border-radius: 999px; background: transparent; color: var(--ink); font-size: 12px; font-weight: 750; text-decoration: none; cursor: pointer; }
  .actions .dark { border-color: var(--ink); background: var(--ink); color: white; }
  .waiting { display: flex; align-items: center; gap: 9px; color: var(--muted); font-size: 12px; }
  .waiting i { width: 7px; height: 7px; border-radius: 50%; background: var(--aqua); animation: pulse 1.2s infinite; }
  .error { padding: 10px 12px; border-left: 3px solid var(--coral); background: #f9e4e4; color: #842f38; font-size: 13px; }
  @keyframes pulse { 50% { opacity: .35; transform: scale(.75); } }
  @media (max-width: 430px) { .actions { display: grid; grid-template-columns: 1fr; } .actions button, .actions a { justify-content: center; } }
  @media (prefers-reduced-motion: reduce) { .waiting i { animation: none; } }
</style>
